/**
 * StickyStack
 *
 * Owns the pinned-header stack of one scroll container, and the scroll-direction
 * chrome state that rides on it. A page declares which rows pin by tagging them
 * `data-sticky-slice`; this controller measures them in document order and
 * assigns each its cumulative `top` offset, so no slice ever has to know the
 * height of the slices above it.
 *
 * Slices do NOT need to be siblings — they are collected with querySelectorAll,
 * so document order is the only thing that matters. Tag the stable mount (e.g.
 * the `[data-filter-bar-mount]` div, not the `.results-filter-bar` inside it)
 * so the slice survives its content being re-rendered.
 *
 * Paint order is inverted relative to DOM order: the first slice sits on top,
 * because every later slice slides *under* it. JS writes only the index
 * (`--sticky-slice-index`); `components/sticky-stack.css` derives the z-index
 * from `--z-sticky` so the sticky band has a single source of truth.
 *
 * Slices that are absent or zero-height need no special handling — they
 * contribute 0 to the offset, which is the correct answer for an empty filter
 * bar mount or a loading branch that renders no mount at all.
 *
 * ── Collapsing chrome ──
 * A page opts one row into scroll-collapse by additionally tagging it
 * `data-sticky-collapse` (in practice the `.page-toggle-row`). Scrolling DOWN
 * then hides that row and lifts everything below it snug under the page title;
 * scrolling UP restores it and hides the page's `.page-fixed-action` bottom
 * button instead, so only one piece of chrome is ever in the way. State is
 * published as two attributes on the scroll container —
 * `data-chrome-collapsed` and `data-chrome-action-hidden` — and the motion
 * itself is CSS (`components/sticky-stack.css`, `components/page-fixed-action.css`),
 * gated to mobile widths and to `prefers-reduced-motion: no-preference`.
 *
 * The rows are displaced with `transform` and stay in flow, so nothing reflows
 * and the list underneath never jumps. Visual hiding is paired with `inert` in
 * one place (`_setCollapsed` / `_setActionHidden`), because a transformed or
 * transparent control is still focusable and still in the accessibility tree.
 *
 * The listener is attached only while a `[data-sticky-collapse]` slice exists,
 * so pages without one (/search, the settings pages) pay nothing.
 *
 * @example
 * // In a section's render() and at the top of its activate():
 * this.stickyStack?.destroy();
 * this.stickyStack = new StickyStack(this.container.querySelector('.explore-view--list'));
 *
 * // In deactivate():
 * this.stickyStack?.destroy();
 * this.stickyStack = null;
 */

/** Marks a row that pins. Applied by pages, collected in document order. */
const SLICE_SELECTOR = '[data-sticky-slice]';

/** Marks the one slice per page that collapses on a downward scroll. */
const COLLAPSE_SELECTOR = '[data-sticky-collapse]';

/** The page's bottom action bar, a sibling of the scroll container. */
const ACTION_SELECTOR = ':scope > [data-fixed-page-action]';

/** Inline custom property carrying a slice's position in the stack. */
const INDEX_PROPERTY = '--sticky-slice-index';

/**
 * Container-level custom property carrying the collapse displacement. One value
 * for the whole stack: every page has exactly one collapsible row, so every
 * slice that moves moves by that row's height.
 */
const SHIFT_PROPERTY = '--sticky-collapse-shift';

/** Marks the slices that take the shift — the collapsible row and those below. */
const SHIFT_MARKER = 'data-collapse-shift';

/** Published state: the collapsible row is hidden. */
const COLLAPSED_ATTR = 'data-chrome-collapsed';

/** Published state: the bottom action bar is hidden. */
const ACTION_HIDDEN_ATTR = 'data-chrome-action-hidden';

/**
 * Direction hysteresis, in px of cumulative travel. Up is deliberately
 * twitchier than down: reaching for the nav should feel instant, while hiding
 * it should take a decided gesture.
 */
const DOWN_THRESHOLD = 8;
const UP_THRESHOLD = 4;

/**
 * How close to the end of the list still counts as "at the bottom", where the
 * action button is what the user came for and must not be hidden. Roughly a
 * bar's height: a tighter window would be unreachable in practice, since any
 * upward gesture large enough to trigger a hide also moves further from the end
 * than that.
 */
const BOTTOM_EPSILON = 64;

/**
 * Tolerance for the rubber-band guard. Not an equality test: `scrollTop` is
 * fractional on fractional-DPR displays while `scrollHeight`/`clientHeight` are
 * integers, so the true bottom of a list routinely reads a hair past the max
 * and would otherwise be discarded as overscroll.
 */
const OVERSCROLL_EPSILON = 1;

/**
 * How long after the last scroll the bottom action returns. This is what makes
 * the action "visible at rest": scrolling down, reaching the bottom and
 * returning to the top all restore it too, but none of those cover a user who
 * scrolled up and then simply stopped.
 */
const IDLE_RESTORE_MS = 1200;

export class StickyStack {
  /**
   * @param {HTMLElement} scrollContainer - The scrolling element that owns the stack
   */
  constructor(scrollContainer) {
    this.container = scrollContainer || null;

    /** @type {HTMLElement[]} Current slices, in document order. */
    this.slices = [];
    /** @type {number[]} Assigned top offset per slice, parallel to `slices`. */
    this.tops = [];
    /** @type {Set<HTMLElement>} Slices currently handed to the ResizeObserver. */
    this.observedSizes = new Set();

    /** @type {HTMLElement|null} The collapsible row, or null if the page has none. */
    this.collapsibleSlice = null;
    /** @type {HTMLElement|null} The page's bottom action bar, if it has one. */
    this.actionEl = null;

    /**
     * Scroll offset below which the chrome never moves — the band's own height,
     * so the collapse cannot fire while the stack is still resting in flow.
     */
    this.armThreshold = 0;
    /** Direction baseline and accumulated travel since the last state change. */
    this.lastScrollTop = 0;
    this.accum = 0;

    this.resizeObserver = null;
    this.mutationObserver = null;
    this.rafId = null;
    this.scrollRafId = null;
    this.idleTimer = null;
    this.listening = false;
    this.destroyed = false;

    this.boundScroll = () => this._onScroll();

    if (!this.container) {
      this.destroyed = true;
      return;
    }
    this._init();
  }

  /**
   * Wire the recompute triggers and lay out the initial stack synchronously,
   * so the first paint is already correct.
   */
  _init() {
    if (typeof ResizeObserver === 'function') {
      this.resizeObserver = new ResizeObserver(() => this._schedule());
    }

    if (typeof MutationObserver === 'function') {
      // childList only — never `attributes`. This controller writes inline
      // styles to the slices it owns, and watching attributes would make every
      // recompute retrigger itself.
      this.mutationObserver = new MutationObserver(() => this._schedule());
      this.mutationObserver.observe(this.container, { childList: true, subtree: true });
    }

    // The bar is a SIBLING of the scroll container, not a descendant, so it is
    // outside everything the observers above watch. It is rendered with the
    // page shell and replaced only when the whole shell is (which rebuilds this
    // controller too), so resolving it once here is enough.
    this.actionEl = this.container.parentElement?.querySelector(ACTION_SELECTOR) || null;

    this._recompute();
  }

  /**
   * Coalesce every trigger in a frame into one recompute.
   */
  _schedule() {
    if (this.destroyed || this.rafId !== null) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this._recompute();
    });
  }

  /**
   * Force an immediate re-measure. Sections normally don't need this — the
   * mutation/resize observers cover in-place changes — but it is the escape
   * hatch for a layout change neither observer can see.
   */
  refresh() {
    if (!this.destroyed) this._recompute();
  }

  /**
   * Re-collect the slices, assign cumulative offsets and stack indices, size the
   * collapse band, and reconcile the observers against the new set.
   */
  _recompute() {
    if (this.destroyed || !this.container.isConnected) return;

    const next = Array.from(this.container.querySelectorAll(SLICE_SELECTOR));

    // Slices that left the stack (tab switch, list re-render) give back their
    // inline styles and watchers.
    for (const el of this.slices) {
      if (!next.includes(el)) this._releaseSlice(el);
    }

    // ResizeObserver can't be queried for what it already watches, so track the
    // set here. Blindly re-observing every pass would re-fire the initial
    // callback each time and spin the rAF loop forever.
    if (this.resizeObserver) {
      for (const el of Array.from(this.observedSizes)) {
        if (!next.includes(el)) {
          this.resizeObserver.unobserve(el);
          this.observedSizes.delete(el);
        }
      }
      for (const el of next) {
        if (!this.observedSizes.has(el)) {
          this.resizeObserver.observe(el);
          this.observedSizes.add(el);
        }
      }
    }

    const firstCollapsible = next.findIndex((el) => el.matches(COLLAPSE_SELECTOR));
    const collapsible = firstCollapsible === -1 ? null : next[firstCollapsible];

    const tops = [];
    let offset = 0;
    next.forEach((el, i) => {
      tops.push(offset);
      el.style.top = `${offset}px`;
      el.style.setProperty(INDEX_PROPERTY, String(i));
      // The collapsible row and everything under it ride the same displacement:
      // those below must move by the ROW's height, not their own, and they are
      // not its siblings, so no CSS combinator could reach them.
      el.toggleAttribute(SHIFT_MARKER, collapsible !== null && i >= firstCollapsible);
      offset += el.offsetHeight;
    });

    this.slices = next;
    this.tops = tops;

    // The band's own height is the arm threshold. It is conservative by
    // construction — the only non-sticky content above the band is the subtitle
    // row — so the chrome can never move while a slice is still unpinned, and it
    // needs no cached geometry that could go stale as the page changes height.
    this.armThreshold = offset;

    const previous = this.collapsibleSlice;
    this.collapsibleSlice = collapsible;
    if (previous && previous !== collapsible) previous.removeAttribute('inert');

    this.container.style.setProperty(
      SHIFT_PROPERTY,
      collapsible ? `-${collapsible.offsetHeight}px` : '0px'
    );

    // A recompute means the DOM changed, so the previous direction baseline is
    // meaningless. Without this, switching to a shorter tab makes the browser
    // clamp scrollTop, and that clamp reads as an upward gesture — hiding the
    // action button on every tab change.
    this.lastScrollTop = this._clampedScrollTop();
    this.accum = 0;

    this._syncListener(collapsible !== null);
  }

  /**
   * Attach or detach the scroll listener as the page gains or loses its
   * collapsible row. Detaching restores the chrome, so a page can never be left
   * holding a collapsed state nothing will clear.
   * @param {boolean} shouldListen
   */
  _syncListener(shouldListen) {
    if (shouldListen === this.listening) return;

    if (shouldListen) {
      this.container.addEventListener('scroll', this.boundScroll, { passive: true });
      this.listening = true;
      return;
    }

    this.container.removeEventListener('scroll', this.boundScroll);
    this.listening = false;
    this._resetChrome();
  }

  /**
   * @returns {number} Current scroll offset, clamped into the real range.
   */
  _clampedScrollTop() {
    const max = this._maxScrollTop();
    return Math.min(Math.max(this.container.scrollTop, 0), max);
  }

  /**
   * @returns {number} The largest legal scroll offset for this container.
   */
  _maxScrollTop() {
    return Math.max(0, this.container.scrollHeight - this.container.clientHeight);
  }

  /**
   * Coalesce a burst of scroll events into one state evaluation per frame. Uses
   * its own rAF handle — `rafId` belongs to `_recompute` and the two schedule
   * independently.
   */
  _onScroll() {
    if (this.destroyed || this.scrollRafId !== null) return;
    this.scrollRafId = requestAnimationFrame(() => {
      this.scrollRafId = null;
      this._updateChrome();
    });
  }

  /**
   * The direction state machine: one sample in, at most one state change out.
   */
  _updateChrome() {
    if (this.destroyed || !this.container.isConnected) return;

    const raw = this.container.scrollTop;
    const max = this._maxScrollTop();

    // Rubber-band overscroll: no state change AND no baseline update, so the
    // gesture resumes cleanly when the scroller springs back.
    if (raw < -OVERSCROLL_EPSILON || raw > max + OVERSCROLL_EPSILON) return;

    const scrollTop = Math.min(Math.max(raw, 0), max);
    const delta = scrollTop - this.lastScrollTop;
    this.lastScrollTop = scrollTop;

    // A reversal discards the travel banked in the other direction, so a jittery
    // finger can't accumulate its way across a threshold.
    if ((delta < 0) !== (this.accum < 0)) this.accum = 0;
    this.accum += delta;

    // Above the band's own height nothing is pinned yet: the chrome is resting
    // in flow and there is nothing to get out of the way of.
    if (scrollTop <= this.armThreshold) {
      this.accum = 0;
      this._setCollapsed(false);
      this._setActionHidden(false);
      this._clearIdleTimer();
      return;
    }

    const atBottom = scrollTop >= max - BOTTOM_EPSILON;

    if (this.accum >= DOWN_THRESHOLD) {
      this.accum = 0;
      this._setCollapsed(true);
      this._setActionHidden(false);
    } else if (this.accum <= -UP_THRESHOLD) {
      this.accum = 0;
      this._setCollapsed(false);
      // At the end of the list the action is what the user came for.
      if (!atBottom) this._setActionHidden(true);
    }

    this._restartIdleTimer();
  }

  /**
   * Whether a KEYBOARD user is currently inside `el`, and would therefore lose
   * their place if it were hidden (`inert` blurs whatever it contains, dropping
   * focus to <body>).
   *
   * Containment alone is the wrong test: a mouse or touch press leaves
   * `document.activeElement` on the button that was pressed, and the segment
   * buttons live INSIDE the collapsible row — so one tab click would suppress
   * the collapse for the rest of the visit (`handleTabChange` only toggles the
   * active class, it never replaces the button). `:focus-visible` is precisely
   * the distinction the browser already draws between the two.
   *
   * @param {HTMLElement} el
   * @returns {boolean}
   */
  _holdsVisibleFocus(el) {
    const active = document.activeElement;
    return !!active && el.contains(active) && active.matches(':focus-visible');
  }

  /**
   * Show or hide the collapsible row. Visual state and `inert` move together:
   * a translated row is still focusable and still in the accessibility tree,
   * and tabbing into one would make the browser scroll it back into view.
   * @param {boolean} collapsed
   */
  _setCollapsed(collapsed) {
    const row = this.collapsibleSlice;
    if (!row) return;
    if (collapsed && this._holdsVisibleFocus(row)) return;

    this.container.toggleAttribute(COLLAPSED_ATTR, collapsed);
    row.toggleAttribute('inert', collapsed);
  }

  /**
   * Show or hide the bottom action bar, on the same visual/`inert` contract.
   * @param {boolean} hidden
   */
  _setActionHidden(hidden) {
    if (!this.actionEl) return;
    if (hidden && this._holdsVisibleFocus(this.actionEl)) return;

    this.container.toggleAttribute(ACTION_HIDDEN_ATTR, hidden);
    this.actionEl.toggleAttribute('inert', hidden);
  }

  /**
   * Bring the action bar back once scrolling stops, so it is never left parked
   * off-screen just because the user's last gesture happened to be upward.
   */
  _restartIdleTimer() {
    this._clearIdleTimer();
    this.idleTimer = setTimeout(() => {
      this.idleTimer = null;
      if (this.destroyed) return;
      this._setActionHidden(false);
    }, IDLE_RESTORE_MS);
  }

  _clearIdleTimer() {
    if (this.idleTimer !== null) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  /**
   * Return both bars to their visible state and drop any pending restore.
   */
  _resetChrome() {
    this._clearIdleTimer();
    this.container.removeAttribute(COLLAPSED_ATTR);
    this.container.removeAttribute(ACTION_HIDDEN_ATTR);
    this.collapsibleSlice?.removeAttribute('inert');
    this.actionEl?.removeAttribute('inert');
    this.accum = 0;
  }

  /**
   * Hand a slice back everything this controller gave it.
   * @param {HTMLElement} el - The slice
   */
  _releaseSlice(el) {
    if (this.resizeObserver && this.observedSizes.has(el)) {
      this.resizeObserver.unobserve(el);
      this.observedSizes.delete(el);
    }

    el.style.removeProperty('top');
    el.style.removeProperty(INDEX_PROPERTY);
    el.removeAttribute(SHIFT_MARKER);
    el.removeAttribute('inert');
  }

  /**
   * Tear down every observer and listener and strip the inline positioning and
   * chrome state this controller applied. Safe to call more than once.
   */
  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;

    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    if (this.scrollRafId !== null) {
      cancelAnimationFrame(this.scrollRafId);
      this.scrollRafId = null;
    }

    this.mutationObserver?.disconnect();
    this.mutationObserver = null;

    this.container.removeEventListener('scroll', this.boundScroll);
    this.listening = false;
    this._resetChrome();
    this.container.style.removeProperty(SHIFT_PROPERTY);

    for (const el of this.slices) this._releaseSlice(el);

    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    this.observedSizes.clear();
    this.slices = [];
    this.tops = [];
    this.collapsibleSlice = null;
    this.actionEl = null;
  }
}
